import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { PageOneFormData } from "../models";

const schema = yup.object().shape({
  creationInfo: yup.object().shape({
    datetime: yup.date().required(),
    createdBy: yup.string(),
  }),
  identifyingPersons: yup.object({
    relationship: yup.string(),
    surname: yup.string().required("Surname is required"),
    homePhone: yup.string(),
    personStatus: yup.string(),
    mobilePhone: yup.string(),
    name: yup.string().required("Name is required"),
    workPhone: yup.string(),
  }),
  safetyMeasures: yup.object({
    serviceIntroduced: yup.boolean(),
    bedRailsUp: yup.boolean(),
    roomIntroduced: yup.boolean(),
    bedsideArrangement: yup.boolean(),
  }),
  wristbandInfo: yup.string(),
  roomSystems: yup.object({
    oxygenSystem: yup.string(),
    windows: yup.string(),
    bedBrake: yup.string(),
    companionRequirement: yup.string(),
  }),
  patientAssessment: yup.object().shape({
    admissionType: yup.string(),
    admissionDescription: yup.string(),
    consciousnessStatus: yup.string(),
    consciousnessDescription: yup.string(),
    sleepPattern: yup.string(),
    sleepDescription: yup.string(),
    defecation: yup.string(),
    defecationDescription: yup.string(),
    habits: yup.string(),
    habitsDescription: yup.string(),
    referringDepartment: yup.string(),
    referringDepartmentDescription: yup.string(),
    interpreterRequirement: yup.string(),
    interpreterDescription: yup.string(),
    hearingSpeech: yup.string(),
    hearingSpeechDescription: yup.string(),
    vision: yup.string(),
    visionDescription: yup.string(),
    prostheses: yup.string(),
    prosthesesDescription: yup.string(),
    communication: yup.string(),
    communicationDescription: yup.string(),
    psychological: yup.string(),
    psychologicalDescription: yup.string(),
    musculoskeletalSystem: yup.string(),
    musculoskeletalDescription: yup.string(),
    peripheralVascularSystem: yup.string(),
    peripheralVascularDescription: yup.string(),
    mobility: yup.string(),
    mobilityDescription: yup.string(),
    mouthTongue: yup.string(),
    mouthTongueDescription: yup.string(),
    respiration: yup.string(),
    respirationDescription: yup.string(),
    urinary: yup.string(),
    urinaryDescription: yup.string(),
    gastrointestinal: yup.string(),
    gastrointestinalDescription: yup.string(),
    nails: yup.string(),
    nailsDescription: yup.string(),
    hair: yup.string(),
    hairDescription: yup.string(),
    bleeding: yup.string(),
    bleedingDescription: yup.string(),
    cardiovascular: yup.string(),
    cardiovascularDescription: yup.string(),
    skin: yup.string(),
    skinDescription: yup.string(),
    environmentOrientation: yup.string(),
    environmentOrientationDescription: yup.string(),
  }),
  habits: yup.object({
    reading: yup.string(),
    radioTv: yup.string(),
    other: yup.string(),
  }),
  physicalFindings: yup.object({
    hasEdema: yup.boolean(),
    edemaDescription: yup.string(),
    hasWound: yup.boolean(),
    woundDescription: yup.string(),
    hasPressureInjury: yup.boolean(),
    pressureInjuryDescription: yup.string(),
  }),
});

const defaultValues: PageOneFormData = {
  creationInfo: {
    datetime: new Date(),
    createdBy: "",
  },
  identifyingPersons: {
    relationship: "",
    surname: "",
    homePhone: "",
    personStatus: "",
    mobilePhone: "",
    name: "",
    workPhone: "",
  },
  safetyMeasures: {
    serviceIntroduced: false,
    bedRailsUp: false,
    roomIntroduced: false,
    bedsideArrangement: false,
  },
  wristbandInfo: "",
  roomSystems: {
    oxygenSystem: "",
    windows: "",
    bedBrake: "",
    companionRequirement: "",
  },
  patientAssessment: {
    admissionType: "",
    admissionDescription: "",
    consciousnessStatus: "",
    consciousnessDescription: "",
    sleepPattern: "",
    sleepDescription: "",
    defecation: "",
    defecationDescription: "",
    habits: "",
    habitsDescription: "",
    referringDepartment: "",
    referringDepartmentDescription: "",
    interpreterRequirement: "",
    interpreterDescription: "",
    hearingSpeech: "",
    hearingSpeechDescription: "",
    vision: "",
    visionDescription: "",
    prostheses: "",
    prosthesesDescription: "",
    communication: "",
    communicationDescription: "",
    psychological: "",
    psychologicalDescription: "",
    musculoskeletalSystem: "",
    musculoskeletalDescription: "",
    peripheralVascularSystem: "",
    peripheralVascularDescription: "",
    mobility: "",
    mobilityDescription: "",
    mouthTongue: "",
    mouthTongueDescription: "",
    respiration: "",
    respirationDescription: "",
    urinary: "",
    urinaryDescription: "",
    gastrointestinal: "",
    gastrointestinalDescription: "",
    nails: "",
    nailsDescription: "",
    hair: "",
    hairDescription: "",
    bleeding: "",
    bleedingDescription: "",
    cardiovascular: "",
    cardiovascularDescription: "",
    skin: "",
    skinDescription: "",
    environmentOrientation: "",
    environmentOrientationDescription: "",
  },
  habits: {
    reading: "",
    radioTv: "",
    other: "",
  },
  physicalFindings: {
    hasEdema: false,
    edemaDescription: "",
    hasWound: false,
    woundDescription: "",
    hasPressureInjury: false,
    pressureInjuryDescription: "",
  },
};

export const usePageOneForm = () => {
  const formMethods = useForm<PageOneFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues,
  });

  const { handleSubmit, setValue } = formMethods;

  const normalizeData = (data: PageOneFormData): PageOneFormData => {
    // Normalization logic (trim and lowercase where applicable)
    return {
      ...data,
      identifyingPersons: {
        ...data.identifyingPersons,
        name: data.identifyingPersons.name.trim(),
        surname: data.identifyingPersons.surname.trim(),
      },
      patientAssessment: Object.entries(data.patientAssessment).reduce(
        (acc, [key, value]) => {
          acc[key as keyof PageOneFormData["patientAssessment"]] =
            typeof value === "string" ? value.trim() : value;
          return acc;
        },
        {} as any
      ),
      habits: {
        reading: data.habits.reading.trim(),
        radioTv: data.habits.radioTv.trim(),
        other: data.habits.other.trim(),
      },
      physicalFindings: {
        ...data.physicalFindings,
        edemaDescription: data.physicalFindings.edemaDescription.trim(),
        woundDescription: data.physicalFindings.woundDescription.trim(),
        pressureInjuryDescription:
          data.physicalFindings.pressureInjuryDescription.trim(),
      },
    };
  };

  const onSubmit = (data: PageOneFormData) => {
    const normalizedData = normalizeData(data);
    console.log("Submitting:", normalizedData);
    // Add API call here
  };

  return {
    formMethods,
    onSubmit: handleSubmit(onSubmit),
  };
};
